import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";

const KIND_ICONS = {
  story_saved: "✦",
  family_request: "?",
  reaction: "♥",
  story_week_day_complete: "✦",
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const ref = useRef();

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data?.items || []);
      setUnread(res.data?.unread || 0);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000); // poll every 45s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch (e) { /* ignore */ }
      setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/mark_all_read"); } catch (e) { /* ignore */ }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell-container">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 hover:bg-[#F0EAE0] rounded-full transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell size={18} className="text-[#111D33]" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center text-[10px] font-medium"
            style={{ background: "#B8973A", color: "#FAF7F2", minWidth: 18, height: 18, borderRadius: 999, padding: "0 5px" }}
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white hairline-cream shadow-lg z-50"
          style={{ borderRadius: 4 }}
          data-testid="notification-dropdown"
        >
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #F0EAE0" }}>
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em]">NOTIFICATIONS</p>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAll}
                className="label-ui text-[10px] text-[#8C8278] hover:text-[#B8973A] transition-colors"
                data-testid="notification-markall-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p
                className="px-5 py-10 text-center text-[#8C8278] italic"
                style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}
                data-testid="notification-empty"
              >
                No notifications yet.<br />
                <span className="text-xs">The library is quiet.</span>
              </p>
            ) : items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => openNotif(n)}
                className={`w-full text-left px-5 py-4 hover:bg-[#FDF9F1] transition-colors flex gap-3 ${!n.read ? "bg-[#FAF7F2]" : ""}`}
                style={{ borderBottom: "1px solid #F0EAE0" }}
                data-testid={`notification-item-${n.notification_id}`}
              >
                <span
                  className="shrink-0 flex items-center justify-center"
                  style={{ width: 30, height: 30, borderRadius: 999, background: !n.read ? "#B8973A" : "#F0EAE0", color: !n.read ? "#FAF7F2" : "#8C8278", fontFamily: "Cormorant Garamond, serif" }}
                >
                  {KIND_ICONS[n.kind] || "•"}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-[#111D33] mb-1 ${!n.read ? "font-medium" : ""}`}
                    style={{ fontFamily: "Georgia, serif", fontSize: "0.95rem", lineHeight: 1.4 }}
                  >
                    {n.title}
                  </p>
                  {n.body && (
                    <p
                      className="text-[#8C8278] italic truncate"
                      style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "0.95rem" }}
                    >
                      {n.body}
                    </p>
                  )}
                  <p className="label-ui text-[10px] text-[#B8973A] mt-1.5">{relativeTime(n.created_at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
